interface CacheEntry<T> {
  value: T
  expiresAt: number
}

/**
 * IntegrationCache — TTL cache for search/product/price/stock.
 */
export class IntegrationCache {
  private readonly store = new Map<string, CacheEntry<unknown>>()
  private hits = 0
  private misses = 0

  get<T>(key: string): T | null {
    const entry = this.store.get(key)
    if (!entry) {
      this.misses += 1
      return null
    }
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key)
      this.misses += 1
      return null
    }
    this.hits += 1
    return entry.value as T
  }

  set<T>(key: string, value: T, ttlMs: number): void {
    this.store.set(key, { value, expiresAt: Date.now() + Math.max(0, ttlMs) })
  }

  clear(prefix?: string): void {
    if (!prefix) {
      this.store.clear()
      return
    }
    for (const key of this.store.keys()) {
      if (key.startsWith(prefix)) this.store.delete(key)
    }
  }

  hitRate(): number {
    const total = this.hits + this.misses
    if (total === 0) return 0
    return Math.round((this.hits / total) * 10000) / 100
  }

  stats() {
    return { hits: this.hits, misses: this.misses, size: this.store.size, hitRate: this.hitRate() }
  }

  resetStats(): void {
    this.hits = 0
    this.misses = 0
  }
}

export const integrationCache = new IntegrationCache()
